import React, { Dispatch, FC, SetStateAction, createContext, useCallback, useContext, useState } from 'react';
import { noop } from 'ramda-adjunct';
import { createHOCWithHookInside } from '../hoc/createHOCWithHookInside';
import { useMMGRSettings } from '../hoc/withMMGRSettings';
import { RecentSearch } from '../media-manager-sdk';
import { usePrivateMediaProxySearchService } from './usePrivateMediaProxy';

export type RecentSearchesContextType = {
  recentSearches: RecentSearch[];
  setRecentSearches: Dispatch<SetStateAction<RecentSearch[]>>;
  deleteRecentSearch: (id: string) => void;
};

export const RecentSearchesContext = createContext<RecentSearchesContextType>({
  recentSearches: [],
  setRecentSearches: noop,
  deleteRecentSearch: noop,
});

export const RecentSearchesProvider: FC = ({ children }) => {
  const { mmgrSettings } = useMMGRSettings();
  const { searches, metaSiteToken } = mmgrSettings;

  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>(searches);

  const SearchService = usePrivateMediaProxySearchService();

  const deleteRecentSearch = useCallback(
    async (deletedId: string) => {
      await SearchService.bulkDeleteRecentSearches({ recentSearchIds: [deletedId] });
      setRecentSearches((prevSearches) => prevSearches.filter(({ id }) => id !== deletedId));
    },
    [setRecentSearches, SearchService, metaSiteToken],
  );

  return (
    <RecentSearchesContext.Provider
      value={{
        recentSearches,
        setRecentSearches,
        deleteRecentSearch,
      }}
    >
      {children}
    </RecentSearchesContext.Provider>
  );
};

export const useRecentSearches = () => useContext(RecentSearchesContext);

export const withRecentSearches = createHOCWithHookInside('WithRecentSearches', useRecentSearches);
